import type {
  CreateTunnelEnrollmentResponseDto,
  InternalNetworkAgentEnrollmentDto,
  InternalNetworkDeploymentRequirementsDto,
  InternalNetworkSensorReleaseDto,
  TunnelEnrollmentDto,
} from '@terra/shared-contracts-internal-network';

export type InternalNetworkTunnelArtifacts = Pick<
  CreateTunnelEnrollmentResponseDto,
  'advertisedRoutes' | 'artifacts'
>;

export type InternalNetworkSensorEnrollmentRow = Pick<
  InternalNetworkAgentEnrollmentDto,
  'id' | 'deploymentStatus' | 'lastHeartbeatAt' | 'createdAt'
>;

export type InternalNetworkTunnelEnrollmentRow = Pick<
  TunnelEnrollmentDto,
  'id' | 'status' | 'advertisedRoutes' | 'createdAt'
>;

export type InternalNetworkSensorConnectionState = {
  hasEnrollment: boolean;
  sensorOnline: boolean;
  callbackBound: boolean;
  canVerify: boolean;
};

export type InternalNetworkSensorConnectionProgress = {
  completedSteps: boolean[];
  currentStepIndex: number;
};

export type InternalNetworkEnrollmentSecrets = {
  apiKey: string;
  deploymentCommand: string;
  composeYaml: string;
  secretsDeploymentCommand: string;
  secretsComposeYaml: string;
  secretsSetupCommand: string;
  imageVerifyScript: string;
  cosignPublicKeyPem: string | null;
  pinnedImageReference: string;
};

export type InternalNetworkOutboundEndpoint =
  InternalNetworkDeploymentRequirementsDto['outboundEndpoints'][number];

export type InternalNetworkSensorEnrollmentStatus =
  InternalNetworkAgentEnrollmentDto['deploymentStatus'];

export type InternalNetworkTunnelEnrollmentStatus =
  TunnelEnrollmentDto['status'];

export type InternalNetworkSensorReleaseDisplay = {
  version: string;
  imageReference: string;
  imageDigest: string | null;
};

export type InternalNetworkEnrollmentStatusColor =
  | 'green'
  | 'orange'
  | 'red'
  | 'gray'
  | 'yellow';

export const mapEnrollmentDeploymentSecrets = ({
  apiKey,
  deploymentCommand,
  composeYaml,
  secretsDeploymentCommand,
  secretsComposeYaml,
  secretsSetupCommand,
  imageVerifyScript,
  cosignPublicKeyPem,
  pinnedImageReference,
}: InternalNetworkEnrollmentSecrets): InternalNetworkEnrollmentSecrets => ({
  apiKey,
  deploymentCommand,
  composeYaml,
  secretsDeploymentCommand,
  secretsComposeYaml,
  secretsSetupCommand,
  imageVerifyScript,
  cosignPublicKeyPem,
  pinnedImageReference,
});

export const formatOutboundEndpoint = (
  endpoint: Pick<
    InternalNetworkOutboundEndpoint,
    'host' | 'port' | 'protocol' | 'paths'
  >,
): { display: string; copyValue: string } => {
  const base = `${endpoint.protocol}://${endpoint.host}:${endpoint.port}`;
  const pathSuffix =
    endpoint.paths.length > 0 ? ` (${endpoint.paths.join(', ')})` : '';

  return {
    display: `${base}${pathSuffix}`,
    copyValue:
      endpoint.paths.length > 0
        ? `${base}\n${endpoint.paths.join('\n')}`
        : base,
  };
};

export const advertisedRoutesPayload = (
  values: string[],
): { advertisedRoutes?: string[] } =>
  values.length > 0 ? { advertisedRoutes: values } : {};

export const sortByCreatedAtDesc = <TItem extends { createdAt: string }>(
  items: TItem[] | undefined,
): TItem[] =>
  [...(items ?? [])].sort(
    (left, right) =>
      new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
  );

export const hasActiveSensorEnrollment = (
  enrollments: Array<{
    deploymentStatus: InternalNetworkSensorEnrollmentStatus;
  }>,
): boolean =>
  enrollments.some((enrollment) => enrollment.deploymentStatus !== 'REVOKED');

export const hasPendingTunnelEnrollment = (
  enrollments: Array<{ status: InternalNetworkTunnelEnrollmentStatus }>,
): boolean =>
  enrollments.some(
    (enrollment) =>
      enrollment.status === 'PENDING' || enrollment.status === 'OFFLINE',
  );

export const resolveSensorEnrollmentStatusColor = (
  status: InternalNetworkSensorEnrollmentStatus,
): InternalNetworkEnrollmentStatusColor => {
  switch (status) {
    case 'ONLINE':
      return 'green';
    case 'OFFLINE':
      return 'orange';
    case 'REVOKED':
      return 'red';
    default:
      return 'gray';
  }
};

export const resolveTunnelEnrollmentStatusColor = (
  status: InternalNetworkTunnelEnrollmentStatus,
): InternalNetworkEnrollmentStatusColor => {
  switch (status) {
    case 'ACTIVE':
      return 'green';
    case 'OFFLINE':
      return 'red';
    case 'REVOKED':
      return 'gray';
    default:
      return 'yellow';
  }
};

export const resolveSensorConnectionProgress = ({
  hasEnrollment,
  sensorOnline,
  callbackBound,
  canVerify,
}: InternalNetworkSensorConnectionState): InternalNetworkSensorConnectionProgress => {
  const completedSteps = [
    hasEnrollment,
    sensorOnline,
    callbackBound,
    canVerify,
  ];

  return {
    completedSteps,
    currentStepIndex: completedSteps.indexOf(false),
  };
};

export const resolveSensorReleaseDisplay = ({
  latestRelease,
  deploymentRequirements,
  defaultVersionLabel,
}: {
  latestRelease:
    | Pick<
        InternalNetworkSensorReleaseDto,
        'version' | 'imageReference' | 'imageDigest'
      >
    | null
    | undefined;
  deploymentRequirements:
    | Pick<InternalNetworkDeploymentRequirementsDto, 'effectiveSensorRelease'>
    | null
    | undefined;
  defaultVersionLabel: string;
}): InternalNetworkSensorReleaseDisplay => ({
  version:
    latestRelease?.version?.trim() ||
    deploymentRequirements?.effectiveSensorRelease.version ||
    defaultVersionLabel,
  imageReference:
    latestRelease?.imageReference?.trim() ||
    deploymentRequirements?.effectiveSensorRelease.imageReference?.trim() ||
    '',
  imageDigest: latestRelease?.imageDigest ?? null,
});
